import * as R from 'ramda';
import validator from 'validator';
// constants
import * as GC from '../constants';
import { ENUMS } from '../constants/enums';
// helpers
import { getValueFromObject } from './getter';
import { createStringFromArray } from './string';
import { isItemTypeVehicle } from './business-logic';
import {
  getAmousConfigByNameFromWindow,
  getConfigGeneralUomCalcDefaultUomSystemFromWindow,
} from './window';
import {
  isTrue,
  ifElse,
  isZero,
  notEquals,
  isNilOrEmpty,
  isNotNilAndNotEmpty,
} from './helpers';
//////////////////////////////////////////////////

const isOdd = (num) => num % 2;

const curriedParseInt = R.curry((text) => parseInt(text, 10));

const NaNToZero = R.curry((value) => {
  if (isNaN(value)) return 0;

  return value;
});

const NaNToNull = R.curry((value) => {
  if (isNaN(value)) return null;

  return value;
});

const fromNillOrEmptyToZero = (item) => ifElse(isNilOrEmpty(item), 0, item);

const setPercentage = (percent, from) => (
  (percent / 100) * from
);

const toNumber = (num) => Number(num);

const maxNumber = (num1, num2) => R.max(
  NaNToZero(toNumber(num1)),
  NaNToZero(toNumber(num2)),
);

// NOTE: for rounding use mathRoundNumber, check using instead of toFixed in all system
const toFixedFromString = (val, qty = 2) => {
  const stringValue = String(val);

  if (validator.isInt(stringValue)) return val;

  const floatValue = validator.toFloat(stringValue);

  return floatValue.toFixed(R.or(qty, 1));
};

// NOTE: for rounding use mathRoundNumber
const toFixed = (value, digits = 2) => {
  if (R.is(Number, value)) return value.toFixed(digits);

  if (R.is(String, value)) return toFixedFromString(value, digits);

  return value;
};

const calculateMarginPercent = (inp, out) => {
  const sub = R.subtract(inp, out);
  const div = R.or(out, 1);

  return toFixed(R.multiply(R.divide(sub, div), 100));
};

// TODO: check handling undefined and NaN
const mathRoundNumber = (val, digits = 2) => {
  const multiplier = 10 ** digits;

  return Math.round(val * multiplier) / multiplier;
};

const fromKgsToPounds = (val, digits = 4) => {
  const multiplier = 10 ** digits;

  return Math.round(val * 2.20462262185 * multiplier) / multiplier;
};

const fromPoundsToKgs = (val, digits = 4) => {
  const multiplier = 10 ** digits;

  return Math.round((val / 2.20462262185) * multiplier) / multiplier;
};

const fromKgsToMetricTons = (val, digits = 4) => (
  (val / 1000).toFixed(digits)
);

const fromMetricTonsToKgs = (val, digits = 4) => (
  (val * 1000).toFixed(digits)
);

const fromKgsToShortTons = (val, digits = 4) => (
  (val / 907.18474).toFixed(digits)
);

const fromShortTonsToKgs = (val, digits = 4) => (
  (val * 907.18474).toFixed(digits)
);

const poundsKgsFromTo = (to, val) => {
  if (to === GC.UOM_KILOGRAM) {
    return fromPoundsToKgs(val);
  } else if (to === GC.UOM_POUND) {
    return fromKgsToPounds(val);
  }
};

const getWeightWithUomByConfigUomSystem = (storedWeight) => {
  const uomSystem = getConfigGeneralUomCalcDefaultUomSystemFromWindow();

  if (R.equals(uomSystem, GC.METRIC_SYSTEM)) return `${storedWeight} ${GC.UOM_KILOGRAM}`;

  return `${fromKgsToPounds(storedWeight, 2)} ${GC.UOM_POUND}`;
};

const fromMilesToKms = (val, toFixedNumber) => (
  (val * 1.6093).toFixed(R.or(toFixedNumber, 4))
);

const fromKmsToMiles = (val, toFixedNumber) => (
  (val / 1.6093).toFixed(R.or(toFixedNumber, 4))
);

const fromKmsToMilesNumber = (val, toFixedNumber) => (
  toNumber(fromKmsToMiles(val, toFixedNumber))
);

const fromMilesToKmsNumber = (val, toFixedNumber) => (
  toNumber(fromMilesToKms(val, toFixedNumber))
);

const convertDistanceAccordingUom = (val, uom) => ifElse(
  R.equals(uom, GC.UOM_KILOMETER),
  fromMilesToKms,
  fromKmsToMiles,
)(val);

const convertDistanceAccordingUomSystem = (val, system) => {
  if (R.or(R.isNil(system), R.equals(system, GC.METRIC_SYSTEM))) return `${val} ${GC.UOM_KILOMETER}`;

  return `${fromKmsToMiles(val, 2)} ${GC.UOM_MILE}`;
};

const milesKmsFromTo = (to, val, toFixedNumber) => {
  if (to === GC.UOM_KILOMETER) {
    return fromMilesToKms(val, toFixedNumber);
  } else if (to === GC.UOM_MILE) {
    return fromKmsToMiles(val, toFixedNumber);
  }
};

const milesKmsFromToAccordingSystemWithNullable = (
  toUom,
  fromUom,
  value,
  toFixedNumber,
) => {
  if (isNilOrEmpty(value)) return 0;

  if (R.equals(toUom, fromUom)) return value;

  if (R.equals(toUom, GC.UOM_KILOMETER)) {
    return fromMilesToKms(value, toFixedNumber);
  } else if (R.equals(toUom, GC.UOM_MILE)) {
    return fromKmsToMiles(value, toFixedNumber);
  }

  return fromKmsToMiles(value, toFixedNumber);
};

const fromCubicFeetToMeter = (val, toFixedNumber) => (
  (val / 35.3147).toFixed(R.or(toFixedNumber, 4))
);

const fromCubicMeterToFeet = (val, toFixedNumber) => (
  (val * 35.3147).toFixed(R.or(toFixedNumber, 4))
);

const cubicFeetMeterFromTo = (to, val) => {
  if (to === GC.UOM_CUBIC_METERS) {
    return fromCubicFeetToMeter(val);
  } else if (to === GC.UOM_CUBIC_FEET) {
    return fromCubicMeterToFeet(val);
  }
};

const fromCelsiusToFahrenheit = (val, toFixedNumber = 4) => (
  ((val * 1.8) + 32).toFixed(toFixedNumber)
);

const fromFahrenheitToCelsius = (val, toFixedNumber = 4) => (
  ((val - 32) / 1.8).toFixed(toFixedNumber)
);

const weightToKgs = R.cond([
  [
    R.propEq(GC.UOM_KILOGRAM, GC.FIELD_FROM),
    (opt) => getValueFromObject(opt),
  ],
  [
    R.propEq(GC.UOM_POUND, GC.FIELD_FROM),
    (opt) => fromPoundsToKgs(getValueFromObject(opt)),
  ],
  [
    R.propEq(GC.UOM_METRIC_TON, GC.FIELD_FROM),
    (opt) => fromMetricTonsToKgs(getValueFromObject(opt)),
  ],
  [
    R.propEq(GC.UOM_TON_US, GC.FIELD_FROM),
    (opt) => fromShortTonsToKgs(getValueFromObject(opt)),
  ],
  [R.T, R.always(0)],
]);

const kgsToWeight = R.cond([
  [
    R.propEq(GC.UOM_KILOGRAM, GC.FIELD_TO),
    (opt) => getValueFromObject(opt),
  ],
  [
    R.propEq(GC.UOM_POUND, GC.FIELD_TO),
    (opt) => fromKgsToPounds(getValueFromObject(opt)),
  ],
  [
    R.propEq(GC.UOM_METRIC_TON, GC.FIELD_TO),
    (opt) => fromKgsToMetricTons(getValueFromObject(opt)),
  ],
  [
    R.propEq(GC.UOM_TON_US, GC.FIELD_TO),
    (opt) => fromKgsToShortTons(getValueFromObject(opt)),
  ],
  [R.T, R.always(0)],
]);

const calcWeightFromTo = (options) => {
  const { to, from, value, toFixedNumber } = options;

  const fromValue = R.or(from, GC.UOM_KILOGRAM);
  const toValue = R.or(to, GC.UOM_KILOGRAM);
  const valueToConvert = R.or(value, 0);
  const kgs = weightToKgs({ from: fromValue, value: valueToConvert });

  return toFixed(kgsToWeight({ value: kgs, to: toValue }), R.or(toFixedNumber, 4));
};

const weight100Map = {
  [GC.UOM_POUND_100]: GC.UOM_POUND,
  [GC.UOM_KILOGRAM_100]: GC.UOM_KILOGRAM,
};

const calcWeightFromTo100 = (options) => {
  const { to, from, value, toFixedNumber } = options;

  const fromValue = R.or(from, GC.UOM_KILOGRAM);
  const toValue = R.pathOr(GC.UOM_KILOGRAM, [to], weight100Map);
  const valueToConvert = R.or(value, 0);
  const kgs = weightToKgs({ from: fromValue, value: valueToConvert });
  const weight = kgsToWeight({ value: kgs, to: toValue });

  if (isNotNilAndNotEmpty(weight)) {
    return toFixed(R.divide(weight, 100), R.or(toFixedNumber, 4));
  }

  return weight;
};

const calcItemsTotalWeightWithoutQty = (items, shouldUseBillableWeight = true) => {
  const uomSystem = getAmousConfigByNameFromWindow(GC.GENERAL_UOM_CALC_DEFAULT_UOM_SYSTEM);

  const weightType = ifElse(
    R.equals(uomSystem, GC.METRIC_SYSTEM),
    GC.UOM_KILOGRAM,
    GC.UOM_POUND,
  );

  const withBillableWeight = R.any(R.prop(GC.FIELD_BILLABLE_WEIGHT), items);

  const weight = R.reduce(
    (accum, item) => {
      const itemWeight = R.path([GC.FIELD_ITEM_WEIGHT], item);
      const itemBillableWeight = R.pathOr(0, [GC.FIELD_BILLABLE_WEIGHT], item);

      const weightToUse = R.and(withBillableWeight, shouldUseBillableWeight)
        ? maxNumber(itemBillableWeight, itemWeight)
        : itemWeight;

      if (R.isNil(weightToUse)) return accum;

      const itemWeightType = R.pathOr(GC.UOM_KILOGRAM, [GC.FIELD_ITEM_WEIGHT_TYPE], item);

      if (R.equals(itemWeightType, weightType)) return R.add(weightToUse, accum);

      return R.add(accum, calcWeightFromTo({
        to: weightType,
        toFixedNumber: 2,
        value: weightToUse,
        from: itemWeightType,
      }));
    },
    0,
    items,
  );

  return {
    [GC.FIELD_ITEM_WEIGHT_TYPE]: weightType,
    [GC.FIELD_ITEM_WEIGHT]: ifElse(isZero(weight), 0, weight.toFixed(2)),
  };
};

const getItemsTotalWeightInfo = (items) => {
  const { weight, weightType } = calcItemsTotalWeightWithoutQty(items);

  if (isNilOrEmpty(weight)) return null;

  return `${weight} ${weightType}`;
};

const calculateTotalQuantity = R.reduce((accum, item) => {
  let itemQuantity = R.pathOr(0, [GC.FIELD_ITEM_QUANTITY], item);

  const isVehicle = isItemTypeVehicle(item);

  let itemPackageType = R.pathOr(
    GC.ITEM_QUANTITY_UNIT_PIECES,
    [GC.FIELD_ITEM_PACKAGE_TYPE],
    item,
  );

  if (isVehicle) {
    itemQuantity = 1;
    itemPackageType = GC.ITEM_TYPE_VEHICLE;
  }

  if (R.isEmpty(accum)) {
    return {
      [GC.FIELD_ITEM_QUANTITY]: itemQuantity,
      [GC.FIELD_ITEM_PACKAGE_TYPE]: itemPackageType,
    };
  }

  if (R.isNil(itemQuantity)) {
    return accum;
  }

  let accPackageType = R.pathOr(
    GC.ITEM_QUANTITY_UNIT_PIECES,
    [GC.FIELD_ITEM_PACKAGE_TYPE],
    accum,
  );
  const accItemQuantity = R.prop(GC.FIELD_ITEM_QUANTITY, accum);

  if (notEquals(itemPackageType, accPackageType)) {
    accPackageType = GC.ITEM_QUANTITY_UNIT_PIECES;
  }

  const resultQuantity = R.add(accItemQuantity, itemQuantity);

  return {
    [GC.FIELD_ITEM_PACKAGE_TYPE]: accPackageType,
    [GC.FIELD_ITEM_QUANTITY]: ifElse(
      R.equals(accPackageType, GC.ITEM_TYPE_VEHICLE),
      resultQuantity,
      resultQuantity.toFixed(1),
    ),
  };
}, {});

const makeTotalPickupQuantityObjectFromItems = (items) => {
  if (isNilOrEmpty(items)) {
    return {
      [GC.FIELD_ITEM_PACKAGE_TYPE]: null,
      [GC.FIELD_TOTAL_PICKUP_QUANTITY]: null,
    };
  }

  const totalPickupQuantity = R.compose(
    R.ifElse(isZero, R.always(null), mathRoundNumber),
    NaNToZero,
    R.sum,
    R.map(R.propOr(null, GC.FIELD_QUANTITY)),
  )(items);

  const packageType = R.compose(
    R.ifElse(
      R.pathEq(1, ['length']),
      R.head,
      R.always(GC.ITEM_QUANTITY_UNIT_PIECES),
    ),
    R.uniq,
    R.map(R.propOr(null, GC.FIELD_PACKAGE_TYPE)),
  )(items);

  return { packageType, totalPickupQuantity };
};

const getItemsTotalQuantityInfo = (items) => {
  const { quantity, packageType } = calculateTotalQuantity(items);

  if (isNilOrEmpty(quantity)) return null;

  return `${quantity} ${packageType}`;
};

const calculateTotalVolumeWithoutQty = R.reduce((accum, item) => {
  const itemVolume = R.path([GC.FIELD_ITEM_VOLUME], item);
  const itemVolumeUom = R.path([GC.FIELD_ITEM_VOLUME_UOM], item);

  if (R.isNil(itemVolume)) {
    return {
      [GC.ITEMS_VOLUME]: '',
      [GC.ITEMS_VOLUME_UOM]: itemVolumeUom,
    };
  }

  if (R.isEmpty(accum)) {
    return {
      [GC.ITEMS_VOLUME_UOM]: itemVolumeUom,
      [GC.ITEMS_VOLUME]: R.or(itemVolume, 0),
    };
  }

  if (R.isNil(itemVolume)) {
    return accum;
  }

  const accVolumeUom = ifElse(
    notEquals(itemVolumeUom, R.path([GC.ITEMS_VOLUME_UOM], accum)),
    '',
    R.pathOr('', [GC.ITEMS_VOLUME_UOM], accum),
  );
  const accVolumeValue = R.prop(GC.ITEMS_VOLUME, accum);
  const resultValue = R.add(accVolumeValue, itemVolume);

  return {
    [GC.ITEMS_VOLUME_UOM]: accVolumeUom,
    [GC.ITEMS_VOLUME]: resultValue.toFixed(2),
  };
}, {});

const getItemsTotalVolumeInfo = (items) => {
  const { itemsVolume, itemsVolumeUom } = calculateTotalVolumeWithoutQty(items);

  if (isNilOrEmpty(itemsVolume)) return null;

  return `${itemsVolume} ${itemsVolumeUom}`;
};

const calculateTotalDistance = (distances) => {
  const uomSystem = getAmousConfigByNameFromWindow(GC.GENERAL_UOM_CALC_DEFAULT_UOM_SYSTEM);
  const distanceUom = ifElse(
    R.equals(uomSystem, GC.METRIC_SYSTEM),
    GC.UOM_KILOMETER,
    GC.UOM_MILE,
  );
  const totalDistance = R.reduce(
    (accum, distance) => {
      let itemDistance;
      let itemDistanceUom;
      const { manualDistance, manualDistanceUom, systemDistance, systemDistanceUom } = distance;

      if (isNotNilAndNotEmpty(manualDistance)) {
        itemDistance = manualDistance;
        itemDistanceUom = R.or(manualDistanceUom, GC.UOM_MILE);
      } else if (isNotNilAndNotEmpty(systemDistance)) {
        itemDistance = systemDistance;
        itemDistanceUom = R.or(systemDistanceUom, GC.UOM_MILE);
      }

      if (R.isNil(itemDistance)) {
        return accum;
      }

      if (notEquals(itemDistanceUom, distanceUom)) {
        itemDistance = milesKmsFromTo(distanceUom, itemDistance, 2);
      }

      return R.add(itemDistance, accum);
    },
    0,
    distances,
  );

  return {
    [GC.FIELD_TOTAL_TRIP_DISTANCE_UOM]: distanceUom,
    [GC.FIELD_TOTAL_TRIP_DISTANCE]: toFixed(totalDistance, 2),
  };
};

const getLoadTotalDistance = (load, distName) => {
  const events = R.pathOr([], [GC.FIELD_LOAD_STOPS], load);

  const distances = R.compose(
    R.filter((item) => isNotNilAndNotEmpty(item)),
    R.map((item) => R.prop(distName, item)),
  )(R.values(events));

  return calculateTotalDistance(distances);
};

const getStopItemsInfo = (items) => {
  const weightInfo = getItemsTotalWeightInfo(items);
  const quantityInfo = getItemsTotalQuantityInfo(items);
  const volumeInfo = getItemsTotalVolumeInfo(items);

  return createStringFromArray([weightInfo, quantityInfo, volumeInfo], ', ');
};

const getItemsTotals = (items) => ({
  itemsTotal: items.length,
  totalQuantity: getItemsTotalQuantityInfo(items),
  totalWeight: calcItemsTotalWeightWithoutQty(items, false),
  anyTapa: R.any(R.prop(GC.FIELD_TAPA), items),
  anyPharma: R.any(R.prop(GC.FIELD_PHARMA), items),
  anyHazardous: R.any(R.prop(GC.FIELD_ITEM_HAZARDOUS), items),
  anyHighSecurity: R.any(R.prop(GC.FIELD_HIGH_SECURITY), items),
  totalBillableWeight: R.any(R.prop(GC.FIELD_BILLABLE_WEIGHT), items)
    ? calcItemsTotalWeightWithoutQty(items)
    : {},
  hasTemperatureSensor: R.any(
    ({ temperatureSensor }) => R.equals(temperatureSensor, ENUMS.ENUM_YES),
    items,
  ),
  temperatureLow: R.compose(
    R.any(isTrue),
    R.map((item) => {
      const temp = R.prop(GC.FIELD_ITEM_TEMPERATURE_LOW, item);

      if (isNilOrEmpty(temp)) return false;

      return R.lte(
        R.prop(GC.FIELD_ITEM_TEMPERATURE_LOW, item),
        ifElse(R.equals(GC.UOM_FAHRENHEIT, R.prop(GC.FIELD_ITEM_TEMPERATURE_UOM, item)), 32, 0),
      );
    }),
  )(items),
  temperatureHigh: R.compose(
    R.any(isTrue),
    R.map((item) => {
      const temp = R.prop(GC.FIELD_ITEM_TEMPERATURE_HIGH, item);

      if (isNilOrEmpty(temp)) return false;

      return R.gt(
        R.prop(GC.FIELD_ITEM_TEMPERATURE_HIGH, item),
        ifElse(R.equals(GC.UOM_FAHRENHEIT, R.prop(GC.FIELD_ITEM_TEMPERATURE_UOM, item)), 32, 0),
      );
    }),
  )(items),
});

const calcContainersTotalWeightWithoutQty = R.reduce((accum, item) => {
  let itemWeight = R.pathOr(0, [GC.FIELD_FULL_CONTAINER_WEIGHT], item);

  const itemWeightUom = R.pathOr(GC.UOM_KILOGRAM, [GC.FIELD_WEIGHT_UOM], item);

  if (R.isEmpty(accum)) {
    return {
      [GC.FIELD_WEIGHT_UOM]: itemWeightUom,
      [GC.FIELD_FULL_CONTAINER_WEIGHT]: itemWeight,
    };
  }

  if (R.isNil(itemWeight)) {
    return accum;
  }

  const accWeightUom = R.pathOr(GC.UOM_KILOGRAM, [GC.FIELD_WEIGHT_UOM], accum);
  const accWeight = R.prop(GC.FIELD_FULL_CONTAINER_WEIGHT, accum);

  if (notEquals(itemWeightUom, accWeightUom)) {
    itemWeight = calcWeightFromTo({
      toFixedNumber: 2,
      to: accWeightUom,
      value: itemWeight,
      from: itemWeightUom,
    });
  }
  const resultWeight = R.add(accWeight, itemWeight);

  return {
    [GC.FIELD_WEIGHT_UOM]: accWeightUom,
    [GC.FIELD_FULL_CONTAINER_WEIGHT]: resultWeight.toFixed(2),
  };
}, {});

const calculateBillableWeight = (palletSpaces, configBillableWeight) => mathRoundNumber(
  R.multiply(
    ifElse(toNumber(palletSpaces), palletSpaces, 0),
    R.or(configBillableWeight, 0),
  ),
);

const calculateItemsPalletSpaces = R.compose(
  R.sum,
  R.map(R.propOr(0, GC.FIELD_PALLET_SPACES)),
);

export {
  isOdd,
  toFixed,
  toNumber,
  NaNToNull,
  NaNToZero,
  maxNumber,
  weightToKgs,
  kgsToWeight,
  setPercentage,
  fromMilesToKms,
  fromKmsToMiles,
  getItemsTotals,
  milesKmsFromTo,
  curriedParseInt,
  mathRoundNumber,
  fromKgsToPounds,
  fromPoundsToKgs,
  poundsKgsFromTo,
  calcWeightFromTo,
  getStopItemsInfo,
  toFixedFromString,
  fromKgsToShortTons,
  fromShortTonsToKgs,
  fromKgsToMetricTons,
  fromMetricTonsToKgs,
  calcWeightFromTo100,
  fromKmsToMilesNumber,
  fromMilesToKmsNumber,
  fromCubicFeetToMeter,
  fromCubicMeterToFeet,
  cubicFeetMeterFromTo,
  getLoadTotalDistance,
  fromNillOrEmptyToZero,
  calculateMarginPercent,
  calculateTotalQuantity,
  calculateTotalDistance,
  calculateBillableWeight,
  fromCelsiusToFahrenheit,
  fromFahrenheitToCelsius,
  getItemsTotalVolumeInfo,
  getItemsTotalQuantityInfo,
  calculateItemsPalletSpaces,
  convertDistanceAccordingUom,
  calcItemsTotalWeightWithoutQty,
  calculateTotalVolumeWithoutQty,
  getWeightWithUomByConfigUomSystem,
  convertDistanceAccordingUomSystem,
  calcContainersTotalWeightWithoutQty,
  makeTotalPickupQuantityObjectFromItems,
  milesKmsFromToAccordingSystemWithNullable,
};
